/**
 * Per-page title / meta description / H1 for every public marketing route.
 * Titles stay under 60 characters and descriptions under 155 so Google shows
 * them in full. Routes read from here via pageHead(), so copy lives in one place.
 */
import { SITE_URL, salesLocations } from "./seo";

export type PageSeo = { title: string; description: string; h1: string };

const PITCH = "Pitch yourself in 60 seconds and get heard.";

const core: Record<string, PageSeo> = {
  "/": {
    title: "Sales Jobs UK | Hire Salespeople Who Can Sell | Closdd",
    description:
      "The sales job board where candidates pitch themselves. Hear a 60-second pitch before you interview, or record yours and get found. Post a role free.",
    h1: "Stop hiring salespeople from CVs. Start hiring from ability.",
  },
  "/find-jobs": {
    title: "Sales Jobs UK: SDR, BDR, Account Executive Roles | Closdd",
    description:
      "Browse sales jobs across the UK and remote. SDR, BDR, account executive and sales manager roles. Pitch yourself in 60 seconds instead of sending a CV.",
    h1: "Find sales jobs. Pitch yourself, skip the CV.",
  },
  "/for-employers": {
    title: "Hire Salespeople Faster: Post a Sales Job Free | Closdd",
    description:
      "Post a sales role free and hear every candidate's 60-second pitch before you interview. Shortlist in minutes, not days. Built for sales hiring only.",
    h1: "Hear them sell before you interview them.",
  },
  "/for-salespeople": {
    title: "Sales Jobs: Pitch Yourself, Skip the CV | Closdd",
    description:
      "Record one 60-second pitch and reach every employer. No CV polish needed. Apply for SDR, BDR, account executive and sales manager jobs on Closdd.",
    h1: "Don't apply. Pitch yourself.",
  },
  "/how-it-works": {
    title: "How Pitch-Based Sales Hiring Works | Closdd",
    description:
      "Employers post a role, candidates record a 60-second pitch, you listen and shortlist. See how Closdd replaces CV screening for sales hiring.",
    h1: "How hiring from a pitch works",
  },
  "/post-job": {
    title: "Post a Sales Job Free | Closdd",
    description:
      "Post your sales vacancy free in minutes. Candidates reply with a 60-second pitch so you can hear who can sell before you book interviews.",
    h1: "Post a sales role. It's free.",
  },
  "/about": {
    title: "About Closdd: The Job Board Built for Salespeople",
    description:
      "Closdd is a sales-only job board. Instead of CVs, candidates record a short pitch so employers hear communication skills first. Learn why we built it.",
    h1: "Built for people who actually sell.",
  },
  "/faq": {
    title: "Closdd FAQ: Posting Jobs, Pitches and CVs",
    description:
      "Answers on posting a sales job free, whether candidates need a CV, how long the pitch should be, and which sales roles you can post on Closdd.",
    h1: "Frequently asked questions",
  },
  "/contact": {
    title: "Contact Closdd | Sales Hiring Support",
    description:
      "Get in touch with the Closdd team about posting a sales role, candidate pitches or anything else you need help with.",
    h1: "Contact Closdd",
  },
  "/appointment-setting": {
    title: "Outsourced Appointment Setting UK | Closdd",
    description:
      "Closdd hires, trains and manages appointment setters for you. They call your leads, qualify them and book meetings worth having. Get a free sales audit.",
    h1: "Your diary, full.",
  },
  "/sales-training": {
    title: "Sales Team Training UK | Closdd",
    description:
      "Live sales training built on your product and buyers. We audit your team, retrain them, coach on real calls and measure the results. Book a free sales audit.",
    h1: "You have a team. You have a product. It is not selling.",
  },
  "/academy": {
    title: "Closdd Academy: Start a Sales Career | No Experience",
    description:
      "A four-week live sales training programme for beginners. Learn to sell, record your 60-second pitch and get your profile in front of hiring employers.",
    h1: "Go from no experience to a paid career in sales.",
  },
  "/sectors": {
    title: "Sales Jobs by Sector UK | Closdd",
    description:
      "Sales jobs in every UK sector, from recruitment and SaaS to fundraising, finance and property. See what each sector hires for and post a role free.",
    h1: "Sales jobs in every sector.",
  },
};

const categories: Record<string, PageSeo> = {
  "/sales-jobs": {
    title: "Sales Jobs UK: Latest Sales Vacancies | Closdd",
    description:
      "Browse the latest sales jobs in the UK and remote. Every application comes with a 60-second pitch, so you get heard, not just read.",
    h1: "Sales jobs in the UK",
  },
  "/account-executive-jobs": {
    title: "Account Executive Jobs UK | Closdd",
    description:
      "Find account executive jobs in the UK and remote. Pitch yourself in 60 seconds and stand out from CV-only applicants.",
    h1: "Account executive jobs",
  },
  "/sales-executive-jobs": {
    title: "Sales Executive Jobs UK | Closdd",
    description:
      "Browse sales executive jobs across the UK. Record a 60-second pitch and let employers hear you sell before the first interview.",
    h1: "Sales executive jobs",
  },
  "/business-development-jobs": {
    title: "Business Development Jobs UK | Closdd",
    description:
      "Search business development jobs in the UK and remote. Skip the CV pile and pitch yourself to employers hiring BDMs and BDRs.",
    h1: "Business development jobs",
  },
  "/sales-manager-jobs": {
    title: "Sales Manager Jobs UK | Closdd",
    description:
      "Find sales manager jobs in the UK. Show how you lead and sell with a 60-second pitch employers can listen to.",
    h1: "Sales manager jobs",
  },
  "/sdr-jobs": {
    title: "SDR Jobs UK: Sales Development Rep Roles | Closdd",
    description:
      "Browse SDR jobs in the UK and remote. Prove you can handle a cold conversation with a 60-second pitch instead of a generic CV.",
    h1: "SDR jobs",
  },
  "/bdr-jobs": {
    title: "BDR Jobs UK: Business Development Rep Roles | Closdd",
    description:
      "Find BDR jobs in the UK and remote. Record a 60-second pitch and let hiring managers hear how you open conversations.",
    h1: "BDR jobs",
  },
  "/telesales-jobs": {
    title: "Telesales Jobs UK | Closdd",
    description:
      "Browse telesales jobs in the UK and remote. Your voice is your CV: record a 60-second pitch and get heard by employers.",
    h1: "Telesales jobs",
  },
};

const roles = "from SDR and BDR to account executive and sales manager";

function locationSeo(slug: string, name: string): PageSeo {
  if (slug === "remote-uk") {
    return {
      title: "Remote Sales Jobs UK | Closdd",
      description: `Browse remote sales jobs open to UK candidates, ${roles}. Pitch yourself in 60 seconds.`,
      h1: "Remote sales jobs in the UK",
    };
  }
  return {
    title: `Sales Jobs in ${name} | Closdd`,
    description: `Browse sales jobs in ${name}, ${roles}. ${PITCH}`,
    h1: `Sales jobs in ${name}`,
  };
}

export function seoForPath(path: string): PageSeo | undefined {
  if (core[path]) return core[path];
  if (categories[path]) return categories[path];
  const m = /^\/sales-jobs\/([^/]+)$/.exec(path);
  const place = m && salesLocations.find((l) => l.slug === m[1]);
  return place ? locationSeo(place.slug, place.name) : undefined;
}

/** Full <head> payload (title, description, canonical, Open Graph, Twitter) for a route. */
export function pageHead(path: string) {
  const seo = seoForPath(path);
  if (!seo) throw new Error(`No SEO copy for ${path}`);
  const url = `${SITE_URL}${path === "/" ? "" : path}`;
  return {
    meta: [
      { title: seo.title },
      { name: "description", content: seo.description },
      { property: "og:title", content: seo.title },
      { property: "og:description", content: seo.description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { property: "og:site_name", content: "Closdd" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: seo.title },
      { name: "twitter:description", content: seo.description },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}

const cityNotes: Record<string, string> = {
  london:
    "London has the UK's densest market for SaaS, fintech and agency sales, with the most SDR and account executive openings and the highest on-target earnings.",
  manchester:
    "Manchester has a fast-growing tech and digital scene, and its sales teams hire steadily for SDR, BDR and account executive roles.",
  birmingham:
    "Birmingham combines a large professional services sector with a growing tech scene, so sales hiring spans field, inside and telesales roles.",
  leeds:
    "Leeds is a major hub for financial services, legal and tech businesses, and many of its sales teams run large inside sales desks.",
  liverpool:
    "Liverpool has a growing digital and creative sector alongside established telesales and customer-facing teams hiring across the city region.",
  "remote-uk":
    "Remote sales roles let you work with employers anywhere in the UK, and a spoken pitch shows how you come across on a call before you ever meet.",
};

/** ~80-word unique intro for a city page, from the pack's template. */
export function cityIntro(slug: string, name: string): string {
  const where = slug === "remote-uk" ? "remote across the UK" : `in ${name}`;
  return `Closdd lists sales jobs ${where}, from SDR and BDR roles to account executive and sales manager positions. Instead of sending a CV, you record a 60-second pitch, so employers hear how you communicate before they choose who to interview. Browse the sales roles currently open ${where} below, or post a role if you are hiring. ${cityNotes[slug] ?? ""}`.trim();
}

/** Unique intro for a role page so it never reads as a thin listing page. */
export function categoryIntro(plural: string, blurb: string): string {
  return `${blurb} Closdd is a UK sales job board where candidates record a 60-second pitch instead of relying on a CV, so employers hear communication, confidence and sales ability before they interview. If you are looking for ${plural.toLowerCase()}, record your pitch once and it goes with every application, so you are heard, not just read. Employers can post a role free, listen to each pitch and shortlist the people who can actually sell. Browse the live roles below, or explore related sales roles and cities further down the page.`;
}

export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Closdd",
  url: SITE_URL,
  description: "Sales job board where candidates record a 60-second pitch instead of sending a CV.",
};

export function faqJsonLd(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function jsonLdScript(data: unknown) {
  return { type: "application/ld+json", children: JSON.stringify(data) };
}
